import { hasToastAction, runToastAction } from "../utilities/toastActions";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import axios from "axios";
import toast, { Toaster, ToastBar } from "react-hot-toast";
import { AlertTriangle } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FormDesignerProvider } from "../context/FormDesignerContext";
import { CaseCategoriesProvider } from "../context/CaseCategoriesContext";
import {
  AudioMetadataType,
  CaseScore,
  CaseStatus,
  CaseStatusType,
  Case as CaseType,
  ChecklistData,
  ChecklistTemplate,
  PendingSuggestionsChanges,
  RedactionType,
  StatuteType,
  PolicyReference,
  Suggestion,
} from "../db/schema";
import CasesLayout from "./CasesLayout";
import Loading from "./common/Loading";
import { toastOptions } from "./common/toastOptions";
import MaintenanceOverlay from "./MaintenanceOverlay";
import Menu from "./Menu/Menu";
import LibraryUploadTray from "./Files/LibraryUploadTray";
import FormUploadTray from "./Forms/FormUploadTray";
import RedactionUploadTray from "./Redactions/RedactionUploadTray";
import { TutorialProvider } from "../tutorials/TutorialProvider";
import PostTrialSurveyProvider from "./PostTrialSurvey/PostTrialSurveyProvider";
import { isAcademyStage } from "../academy/stage/isStage";

// Academy stage mode: the scripted scene player, loaded only when the app is
// running inside a lesson's player (nothing of it ships in the normal path).
const AcademyStageRunner = lazy(() => import("../academy/stage/AcademyStageRunner"));

function Dashboard() {
  const [newCase, setNewCase] = useState<CaseType | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string>("");
  const [caseContent, setCaseContent] = useState<{ data: string } | undefined>(
    undefined,
  );
  const [hasCases, setHasCases] = useState<boolean>(true);
  const [caseScore, setCaseScore] = useState<CaseScore | undefined>(undefined);
  const [caseSuggestions, setCaseSuggestions] = useState<
    Suggestion[] | undefined
  >([]);
  const [caseAudioUrl, setCaseAudioUrl] = useState<string>("");
  const [isMobileCase, setIsMobileCase] = useState(false);
  const [activeTab, setActiveTab] = useState<"narrative" | "transcription">(
    "narrative",
  );
  const [caseStatus, setCaseStatus] = useState<
    (typeof CaseStatus.enumValues)[number]
  >(CaseStatusType.enum.active);
  const [isLoading, setIsLoading] = useState(true);
  const [caseStatutes, setCaseStatutes] = useState<StatuteType[]>([]);
  const [casePolicyReferences, setCasePolicyReferences] = useState<PolicyReference[]>([]);
  const [caseRedactions, setCaseRedactions] = useState<RedactionType[]>([]);
  const [caseTranscription, setCaseTranscription] = useState<{
    task: string;
    language: string;
    duration: number;
    text: string;
    words: {
      word: string;
      start: number;
      end: number;
    }[];
  } | null>(null);
  const [supplementalTranscription, setSupplementalTranscription] = useState<{
    task: string;
    language: string;
    duration: number;
    text: string;
    words: {
      word: string;
      start: number;
      end: number;
    }[];
  } | null>(null);
  const [caseAudioMetadata, setCaseAudioMetadata] =
    useState<AudioMetadataType | null>(null);
  const [pendingSuggestionsChanges, setPendingSuggestionsChanges] =
    useState<PendingSuggestionsChanges | null>(null);
  const [audioControlsRef, setAudioControlsRef] = useState<{
    rewind: (seconds: number) => void;
    fastForward: (seconds: number) => void;
    play: () => void;
    pause: () => void;
  } | null>(null);
  const [checklist, setChecklist] = useState<{
    template: ChecklistTemplate;
    data: ChecklistData;
    completed_at?: string;
    created_at?: string;
    updated_at?: string;
  } | null>(null);
  const [showMaintenanceOverlay, setShowMaintenanceOverlay] = useState(false);
  const [showMaintenanceBanner, setShowMaintenanceBanner] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [maintenanceTime, setMaintenanceTime] = useState("");
  const [caseFiles, setCaseFiles] = useState<any[]>([]);
  const [discardedCaseId, setDiscardedCaseId] = useState<string | null>(null);
  const [erroredCase, setErroredCase] = useState<{
    caseId: string;
    shouldSelectNext: boolean;
  } | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname.split("/")[1];
  const { user } = useAuth();
  // const { isOfficer, isRecords } = useUserRoles();

  useEffect(() => {
    const checkUser = async () => {
      try {
        if (!user?.name || !user?.department || !user?.badgeID) {
          window.location.href = "/post-signup";
        }
      } finally {
        // Delay removal of loading screen slightly to prevent flash
        setTimeout(() => setIsLoading(false), 100);
      }
    };
    checkUser();
  }, [user]);

  useEffect(() => {
    let f10PressCount = 0;
    let f10Timer: NodeJS.Timeout | null = null;
    let isF10Held = false;
    let f10HoldTimer: NodeJS.Timeout | null = null;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "F9") {
        event.preventDefault();
        // Rewind 2 seconds
        if (audioControlsRef?.rewind) {
          audioControlsRef.rewind(2);
        }
      } else if (event.key === "F10") {
        event.preventDefault();

        if (!isF10Held) {
          isF10Held = true;
          f10PressCount++;

          // Start playing audio
          if (audioControlsRef?.play) {
            audioControlsRef.play();
          }

          // Set timer for double tap detection
          if (f10Timer) {
            clearTimeout(f10Timer);
          }

          f10Timer = setTimeout(() => {
            f10PressCount = 0;
          }, 300); // 300ms window for double tap
        }
      } else if (event.key === "F11") {
        event.preventDefault();
        // Fast forward 2 seconds
        if (audioControlsRef?.fastForward) {
          audioControlsRef.fastForward(2);
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "F10") {
        event.preventDefault();

        if (isF10Held) {
          isF10Held = false;

          // Pause audio
          if (audioControlsRef?.pause) {
            audioControlsRef.pause();
          }

          // Check for double tap
          if (f10PressCount >= 2) {
            // Double tap - 5 second rewind
            if (audioControlsRef?.rewind) {
              audioControlsRef.rewind(5);
            }
            f10PressCount = 0;
            if (f10Timer) {
              clearTimeout(f10Timer);
              f10Timer = null;
            }
          } else {
            // Single release - immediate 2 second rewind
            if (audioControlsRef?.rewind) {
              audioControlsRef.rewind(2);
            }
            // Reset count after a delay to allow for potential double tap
            f10HoldTimer = setTimeout(() => {
              f10PressCount = 0;
            }, 300);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      if (f10Timer) {
        clearTimeout(f10Timer);
      }
      if (f10HoldTimer) {
        clearTimeout(f10HoldTimer);
      }
    };
  }, [audioControlsRef]);

  const handleNewCase = useCallback(
    (
      createdCase: CaseType,
      score: CaseScore | undefined = undefined,
      suggestions: Suggestion[] = [],
      transcription: {
        task: string;
        language: string;
        duration: number;
        text: string;
        words: {
          word: string;
          start: number;
          end: number;
        }[];
      } | null = null,
    ) => {
      setNewCase(createdCase);
      setSelectedCaseId(createdCase.id);
      setCaseContent(createdCase.content as { data: string });
      setHasCases(true);
      setCaseScore(score);
      setCaseSuggestions(suggestions);
      setCaseAudioUrl(createdCase.audioUrl || "");
      setCaseStatus(createdCase.status || CaseStatusType.enum.processing);
      setCaseStatutes(createdCase.statutes || []);
      setCasePolicyReferences(createdCase.policyReferences || []);
      setCaseRedactions(createdCase.redactions || []);
      setCaseTranscription(transcription);
      setCaseAudioMetadata(createdCase.audioMetadata || null);
    },
    [],
  );

  const handleChecklistComplete = useCallback(
    (createdCase: CaseType) => {
      setNewCase(createdCase);
      setSelectedCaseId(createdCase.id);
      setCaseContent(createdCase.content as { data: string });
      setHasCases(true);
      setCaseStatus(createdCase.status || CaseStatusType.enum.checklist);
      navigate(`/cases/${createdCase.id}`);
    },
    [navigate],
  );

  const handleCaseDiscarded = useCallback(
    (caseId: string) => {
      // Trigger Cases component to reload and select next case
      setDiscardedCaseId(caseId);
      // Navigate to cases page if not already there
      if (!location.pathname.startsWith("/cases")) {
        navigate("/cases");
      }
    },
    [location.pathname, navigate],
  );

  const handleCaseError = useCallback(
    (caseId: string, shouldSelectNext: boolean) => {
      // Trigger Cases component to remove the errored case
      // Only select next case if the errored case was currently selected
      setErroredCase({ caseId, shouldSelectNext });
    },
    [],
  );

  const renderContent = () => {
    if (path === "cases" || path === "trash") {
      return (
        <CasesLayout
          selectedCaseId={selectedCaseId}
          setSelectedCaseId={setSelectedCaseId}
          caseContent={caseContent}
          setCaseContent={setCaseContent}
          hasCases={hasCases}
          setHasCases={setHasCases}
          caseScore={caseScore}
          setCaseScore={setCaseScore}
          caseSuggestions={caseSuggestions}
          setCaseSuggestions={setCaseSuggestions}
          newCase={newCase}
          setCaseAudioUrl={setCaseAudioUrl}
          caseAudioUrl={caseAudioUrl}
          isMobileCase={isMobileCase}
          setIsMobileCase={setIsMobileCase}
          caseStatus={caseStatus}
          setCaseStatus={setCaseStatus}
          caseStatutes={caseStatutes}
          setCaseStatutes={setCaseStatutes}
          casePolicyReferences={casePolicyReferences}
          setCasePolicyReferences={setCasePolicyReferences}
          caseRedactions={caseRedactions}
          setCaseRedactions={setCaseRedactions}
          caseTranscription={caseTranscription}
          setCaseTranscription={setCaseTranscription}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          caseAudioMetadata={caseAudioMetadata}
          setCaseAudioMetadata={setCaseAudioMetadata}
          pendingSuggestionsChanges={pendingSuggestionsChanges}
          setPendingSuggestionsChanges={setPendingSuggestionsChanges}
          setAudioControlsRef={setAudioControlsRef}
          supplementalTranscription={supplementalTranscription}
          setSupplementalTranscription={setSupplementalTranscription}
          checklist={checklist}
          setChecklist={setChecklist}
          caseFiles={caseFiles}
          setCaseFiles={setCaseFiles}
          discardedCaseId={discardedCaseId}
          onCaseError={handleCaseError}
          erroredCase={erroredCase}
          onCaseDiscarded={handleCaseDiscarded}
        />
      );
    }
    return <Outlet />;
  };

  useEffect(() => {
    const fetchMaintenanceStatus = async () => {
      try {
        const response = await axios.get("/api/health");
        const { maintenance } = response.data;
        if (maintenance) {
          setShowMaintenanceBanner(maintenance.showBanner);
          setShowMaintenanceOverlay(maintenance.showOverlay);
          setMaintenanceMessage(maintenance.message || "");
          if (maintenance.time) {
            const localTime = new Date(maintenance.time).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
            });
            setMaintenanceTime(localTime);
          }
        }
      } catch {
        // Health check failed — don't change maintenance state
      }
    };

    fetchMaintenanceStatus();
    const interval = setInterval(fetchMaintenanceStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <FormDesignerProvider>
      <CaseCategoriesProvider>
      <PostTrialSurveyProvider>
      <TutorialProvider>
      {isAcademyStage && (
        <Suspense fallback={null}>
          <AcademyStageRunner />
        </Suspense>
      )}
      {showMaintenanceOverlay && (
        <MaintenanceOverlay
          onDismiss={() => setShowMaintenanceOverlay(false)}
        />
      )}
      <div className="flex flex-col h-screen bg-gray-100 dark:bg-base-200">
        {showMaintenanceBanner && (
          <div className="w-full bg-orange-500 text-white text-center py-1 z-50 shadow-md">
            <p className="text-sm font-medium flex items-center justify-center">
              <AlertTriangle className="-ml-1 mr-1.5 h-4 w-4 text-white" />
              {maintenanceMessage}{maintenanceTime ? ` ${maintenanceTime}` : ""}
            </p>
          </div>
        )}
        <div className="flex flex-1 min-h-0">
          {/* Debug: Show user roles in development */}
          {/* {process.env.NODE_ENV === 'development' && (
          <div className="fixed top-0 right-0 z-50 bg-black text-white p-2 text-xs">
            Roles: {user?.roles?.join(', ') || 'None'} |
            Officer: {isOfficer() ? 'Yes' : 'No'} |
            Records: {isRecords() ? 'Yes' : 'No'}
          </div>
        )} */}
          <Menu
            onNewCase={handleNewCase}
            isMobileCase={isMobileCase}
            setIsMobileCase={setIsMobileCase}
            onChecklistComplete={handleChecklistComplete}
            onCaseDiscarded={handleCaseDiscarded}
          />
          {renderContent()}
        </div>
      </div>
      {/* Progress for files being saved to Files; lives here so it survives navigation. */}
      <LibraryUploadTray />
      <FormUploadTray />
      <RedactionUploadTray />
      <Toaster position="bottom-center" toastOptions={toastOptions}>
        {(t) => (
          <div
            style={{
              opacity: t.visible ? 1 : 0,
              transition: "opacity 200ms ease",
              cursor: hasToastAction(t.id) ? "pointer" : undefined,
            }}
            // A click dismisses the toast, unless the toast is a link (see toastActions).
            onClick={() => {
              if (!runToastAction(t.id)) toast.dismiss(t.id);
            }}
          >
            <ToastBar toast={t} style={{ animation: "none" }} />
          </div>
        )}
      </Toaster>
      </TutorialProvider>
      </PostTrialSurveyProvider>
      </CaseCategoriesProvider>
    </FormDesignerProvider>
  );
}

export default Dashboard;
